import { useEffect } from "react";
import { FiMapPin, FiUser, FiPhone, FiTruck, FiCheck } from "react-icons/fi";
import { BiCheck } from "react-icons/bi";

/**
 * @typedef {Object} ShippingAddress
 * @property {string} id
 * @property {string} street_address
 * @property {string} google_address_id
 * @property {string} suburb
 * @property {string} city
 * @property {string} province
 * @property {string} country
 * @property {string} country_code
 * @property {number} distance_in_meters
 * @property {number} latitude
 * @property {number} longitude
 * @property {string} recipient_name
 * @property {string} recipient_phone
 * @property {boolean} is_default
 * @param {Object} props
 * @param {ShippingAddress} props.selectedShippingAddress
 * @param {React.Dispatch<React.SetStateAction<ShippingAddress>>} props.setSelectedShippingAddress
 * @param {ShippingAddress[]} props.shippingAddresses
 */
const ShippingAddressToUse = (props) => {
	const { selectedShippingAddress, setSelectedShippingAddress, shippingAddresses = [] } = props;

	// Check if addresses exist before accessing
	const defaultShippingAddress =
		shippingAddresses?.find((addr) => addr.is_default) || shippingAddresses?.[0];

	useEffect(() => {
		// Only set if not already selected and addresses exist
		if (defaultShippingAddress && !selectedShippingAddress) {
			setSelectedShippingAddress(defaultShippingAddress);
		}
	}, [defaultShippingAddress, setSelectedShippingAddress, selectedShippingAddress]);

	const handleSelectAddress = (address) => {
		setSelectedShippingAddress(address);
	};

	// Calculate estimated delivery time based on distance
	const getDeliveryEstimate = (distanceMeters) => {
		if (!distanceMeters) return "Calculating...";
		const km = distanceMeters / 1000;
		if (km < 10) return "1-2 days";
		if (km < 50) return "2-3 days";
		if (km < 200) return "3-5 days";
		return "5-7 days";
	};

	// Don't render if no addresses
	if (!shippingAddresses?.length) {
		return (
			<div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
				<FiMapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
				<p className="text-gray-500">No saved delivery addresses</p>
				<p className="text-sm text-gray-400 mt-1">Add a new address below to continue</p>
			</div>
		);
	}

	return (
		<div className="w-full space-y-3">
			{/* Address Cards */}
			{shippingAddresses.map((address) => {
				const isSelected = selectedShippingAddress?.id === address.id;
				const isDefault = address.is_default;

				return (
					<button
						key={address.id}
						type="button"
						onClick={() => handleSelectAddress(address)}
						className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
							isSelected
								? "border-akgreener bg-akgreen/5 shadow-md"
								: "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
						}`}
					>
						<div className="flex items-start gap-3">
							{/* Radio indicator */}
							<div
								className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center mt-0.5 transition-all ${
									isSelected ? "border-akgreener bg-akgreener" : "border-gray-300 bg-white"
								}`}
							>
								{isSelected && <BiCheck className="w-4 h-4 text-white" />}
							</div>

							{/* Address Details */}
							<div className="flex-1 min-w-0">
								{/* Nickname and Badges */}
								<div className="flex items-center gap-2 mb-1 flex-wrap">
									<span className="font-bold text-gray-900">{address.nickname || "Address"}</span>
									{isDefault && (
										<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-akgreen/10 text-akgreen">
											Default
										</span>
									)}
									{isSelected && (
										<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
											<FiCheck className="w-3 h-3 mr-0.5" />
											Selected
										</span>
									)}
								</div>

								{/* Street Address */}
								<div className="flex items-start gap-2 text-sm text-gray-600 mb-2">
									<FiMapPin className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-400" />
									<div>
										<p className="line-clamp-1">
											{address.street_address}
											{address.suburb && `, ${address.suburb}`}
										</p>
										<p className="text-gray-500">
											{address.city}
											{address.province && `, ${address.province}`}
											{address.country && ` - ${address.country}`}
										</p>
									</div>
								</div>

								{/* Recipient Info */}
								<div className="flex flex-wrap gap-4 text-xs text-gray-500">
									<div className="flex items-center gap-1.5">
										<FiUser className="w-3.5 h-3.5" />
										<span>{address.recipient_name}</span>
									</div>
									{address.recipient_phone && (
										<div className="flex items-center gap-1.5">
											<FiPhone className="w-3.5 h-3.5" />
											<span>{address.recipient_phone}</span>
										</div>
									)}
								</div>

								{/* Delivery Estimate */}
								{address.distance_in_meters && (
									<div className="mt-3 pt-3 border-t border-gray-100">
										<div className="flex items-center gap-2 text-xs">
											<FiTruck className="w-4 h-4 text-akgreener" />
											<span className="text-gray-600">
												Estimated delivery:
												<span className="font-medium text-akgreener ml-1">
													{getDeliveryEstimate(address.distance_in_meters)}
												</span>
											</span>
											<span className="text-gray-400">
												({(address.distance_in_meters / 1000).toFixed(1)} km away)
											</span>
										</div>
									</div>
								)}
							</div>
						</div>
					</button>
				);
			})}

			{/* Selection Summary */}
			{selectedShippingAddress && shippingAddresses.length > 1 && (
				<div className="flex items-center gap-2 text-xs text-gray-500 pt-2">
					<FiCheck className="w-4 h-4 text-akgreener" />
					<span>
						Delivering to{" "}
						<span className="font-medium text-gray-700">
							{selectedShippingAddress.nickname || selectedShippingAddress.city}
						</span>
					</span>
				</div>
			)}
		</div>
	);
};

export default ShippingAddressToUse;
